import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

function TwoFactorAuth() {
  const [status, setStatus] = useState({ two_factor_enabled: false, enabled_at: null });
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [disableData, setDisableData] = useState({ password: '', code: '' });
  const [disabling, setDisabling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/auth/2fa/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error loading 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/auth/2fa/setup', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(response.data);
      setShowSetupModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/auth/2fa/verify-setup', 
        { code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('2FA enabled successfully!');
      setShowSetupModal(false);
      setSetupData(null);
      setVerifyCode('');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (disableData.code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setDisabling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/auth/2fa/disable', 
        disableData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('2FA disabled successfully');
      setShowDisableModal(false);
      setDisableData({ password: '', code: '' });
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            status.two_factor_enabled 
              ? 'bg-green-500/10' 
              : 'bg-yellow-500/10'
          }`}>
            {status.two_factor_enabled ? (
              <ShieldCheck className="w-6 h-6 text-green-500" />
            ) : (
              <Shield className="w-6 h-6 text-yellow-500" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-lg">Two-Factor Authentication</h3>
            <p className="text-sm text-neutral-400">
              {status.two_factor_enabled 
                ? 'Your account is protected with 2FA' 
                : 'Add an extra layer of security to your account'}
            </p>
          </div>
        </div>

        {status.two_factor_enabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-500 bg-green-500/10 px-4 py-3 rounded-lg">
              <ShieldCheck className="w-5 h-5" />
              <span className="font-medium">2FA is enabled</span>
              {status.enabled_at && (
                <span className="text-sm text-green-400/70 ml-auto">
                  Since {new Date(status.enabled_at).toLocaleDateString()}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowDisableModal(true)}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              Disable 2FA
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-yellow-500 bg-yellow-500/10 px-4 py-3 rounded-lg">
              <Shield className="w-5 h-5" />
              <span>2FA is not enabled</span>
            </div>
            <Button
              onClick={startSetup}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Enable 2FA
            </Button>
          </div>
        )}
      </div>

      {/* Setup Modal */}
      <Dialog open={showSetupModal} onOpenChange={setShowSetupModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>

          {setupData && (
            <div className="space-y-6">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl">
                  <img 
                    src={setupData.qr_code} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual Entry Key */}
              <div className="space-y-2">
                <button
                  onClick={() => setShowManualKey(!showManualKey)}
                  className="text-sm text-[#00F0FF] hover:underline"
                >
                  {showManualKey ? 'Hide' : 'Show'} manual entry key
                </button>
                {showManualKey && (
                  <div className="flex items-center gap-2 bg-neutral-800 p-3 rounded-lg">
                    <code className="text-sm text-[#00F0FF] flex-1 break-all">
                      {setupData.manual_entry_key}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(setupData.manual_entry_key)}
                      className="hover:bg-white/10"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>

              {/* Verify Code */}
              <div className="space-y-2">
                <label className="text-sm text-neutral-400">
                  Enter the 6-digit code from your app
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="bg-neutral-800 border-white/10 text-center text-2xl tracking-widest font-mono"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSetupModal(false);
                    setSetupData(null);
                    setVerifyCode('');
                  }}
                  className="flex-1 border-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={verifySetup}
                  disabled={verifying || verifyCode.length !== 6}
                  className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Enable'
                  )}
                </Button>
              </div>

              {/* Supported Apps */}
              <p className="text-xs text-neutral-500 text-center">
                Works with Google Authenticator, Authy, 1Password, and other TOTP apps
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Modal */}
      <Dialog open={showDisableModal} onOpenChange={setShowDisableModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <ShieldOff className="w-5 h-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              This will make your account less secure. Please confirm by entering your password and current 2FA code.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm text-neutral-400">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={disableData.password}
                  onChange={(e) => setDisableData({ ...disableData, password: e.target.value })}
                  placeholder="Enter your password"
                  className="bg-neutral-800 border-white/10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 2FA Code */}
            <div className="space-y-2">
              <label className="text-sm text-neutral-400">Current 2FA Code</label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={disableData.code}
                onChange={(e) => setDisableData({ ...disableData, code: e.target.value.replace(/\D/g, '') })}
                placeholder="000000"
                className="bg-neutral-800 border-white/10 text-center text-2xl tracking-widest font-mono"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDisableModal(false);
                  setDisableData({ password: '', code: '' });
                }}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={disable2FA}
                disabled={disabling || !disableData.password || disableData.code.length !== 6}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold"
              >
                {disabling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Disabling...
                  </>
                ) : (
                  'Disable 2FA'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TwoFactorAuth;
